import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { FuseConfirmationDialogComponent } from '@fuse/services/confirmation/dialog/dialog.component';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, map, of, tap } from 'rxjs';

import { UserService } from '../../user/user.service';

const API_KEYS_URL = '/settings/general/api-keys';
const EXEMPT_URLS = [API_KEYS_URL, '/settings/shops'];

export const UzumApiKeyGuard: CanActivateFn | CanActivateChildFn = (
    route,
    state
) => {
    const router: Router = inject(Router);
    const dialog = inject(MatDialog);
    const translocoService = inject(TranslocoService);
    const userService = inject(UserService);

    if (
        userService.isEmployee ||
        userService.planExpired ||
        !userService.uzumApiKeyRequired ||
        userService.uzumApiKeyDialogShown ||
        userService.uzumApiKeys.some((apiKey) => apiKey.status === 'ACTIVE') ||
        EXEMPT_URLS.some((url) => state.url.startsWith(url))
    ) {
        return true;
    }

    const t = (key: string) =>
        translocoService.translate('admin.settings.general.api_keys.dialog.' + key);

    return userService.getShops().pipe(
        tap((shops) => {
            if (!shops.length || dialog.openDialogs.length) {
                return;
            }

            dialog
                .open(FuseConfirmationDialogComponent, {
                    autoFocus: false,
                    disableClose: true,
                    closeOnNavigation: false,
                    panelClass: 'fuse-confirmation-dialog-panel',
                    data: {
                        title: t('title'),
                        message: t('message'),
                        icon: { show: true, name: 'heroicons_outline:key', color: 'warning' },
                        actions: {
                            confirm: { show: true, label: t('confirm'), color: 'primary' },
                            cancel: { show: true, label: t('cancel') },
                        },
                        dismissible: false,
                    },
                })
                .afterClosed()
                .subscribe((result) => {
                    userService.uzumApiKeyDialogShown = true;

                    if (result === 'confirmed') {
                        router.navigateByUrl(API_KEYS_URL);
                    }
                });
        }),
        map(() => true),
        catchError(() => of(true))
    );
};
